import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { useAuth } from '@/auth/context'
import { TenantContext } from './context'
import type { TenantMembership, TenantValue } from './context'

/**
 * Which organisations this user belongs to, and which one the session is in.
 *
 * The two halves come from different places on purpose. The **list** is
 * `GET /api/v1/tenants`, scoped by the caller's own user id. The **active one**
 * is `session.tenant_id`, because the session is the authority — a request that
 * names any other tenant is refused with `TENANT_MISMATCH` no matter what this
 * SPA believes (`api/app/tenancy/dependencies.py`). Keeping a separate
 * client-side "current organisation" would be a second answer to a question
 * that already has one, and the two would eventually disagree.
 *
 * So `select()` posts the switch and then re-reads `/auth/me`, rather than
 * optimistically updating: the switch either landed in the session row or it
 * did not, and a UI that showed the new organisation while the server still
 * held the old one would 403 on the very next request.
 *
 * **`adopt()` exists because a refetch is not instant.** Creating an
 * organisation and accepting an invitation both return the tenant *and* select
 * it on the session, so at that moment the SPA knows both halves — but a
 * `reload()` only *starts* a request. Navigating into the shell before it
 * lands means `RequireTenant` sees a session pointing at an organisation that
 * is not in the list yet and sends the user straight back to the picker. That
 * is not hypothetical: it is what the first live run of `e2e/shell.spec.ts`
 * did, on a signup that had otherwise worked perfectly.
 *
 * Written with `useState` rather than `useQuery` deliberately. This is the one
 * read in the app that is auth-dependent, adoptable, and must survive a
 * refetch without emptying — three exceptions to what that hook is for, and
 * bending it to fit would make it worse for the twenty ordinary reads.
 */
export function TenantProvider({ children }: { children: ReactNode }): JSX.Element {
  const { status, session, refresh } = useAuth()
  const activeId = session?.tenant_id ?? null
  const authenticated = status === 'authenticated'

  const [memberships, setMemberships] = useState<TenantMembership[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [attempt, setAttempt] = useState(0)

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!authenticated) {
      // No request worth making, and the previous user's organisations are
      // discarded so the next person to sign in on this browser never sees them.
      setMemberships(null)
      setFailure(null)
      return
    }

    const controller = new AbortController()
    void (async () => {
      try {
        const result = await call(() => api.GET('/api/v1/tenants', { signal: controller.signal }))
        if (controller.signal.aborted || !mounted.current) return
        if (result.ok) {
          setMemberships(result.data.tenants)
          setFailure(null)
        } else {
          setFailure(result.failure)
        }
      } catch {
        // `call()` re-throws only AbortError, which is this effect cleaning up.
      }
    })()

    return () => {
      controller.abort()
    }
  }, [authenticated, attempt])

  const reload = useCallback(() => {
    setAttempt((n) => n + 1)
  }, [])

  const adopt = useCallback((row: TenantMembership) => {
    setMemberships((current) =>
      // Ordered by name, matching what `GET /api/v1/tenants` returns, so the
      // switcher does not reshuffle when the real list arrives a moment later.
      [...(current ?? []).filter((existing) => existing.tenant.id !== row.tenant.id), row].sort(
        (a, b) => a.tenant.name.localeCompare(b.tenant.name),
      ),
    )
    setFailure(null)
  }, [])

  const select = useCallback(
    async (tenantId: string) => {
      const result = await call(() =>
        api.POST('/api/v1/tenants/{tenant_id}/select', {
          params: { path: { tenant_id: tenantId } },
        }),
      )
      if (!result.ok) return false
      await refresh()
      return true
    },
    [refresh],
  )

  /**
   * Signing in creates a **new** session, and a new session has no organisation
   * on it — `POST /auth/login` has no way to know which one to pick. For
   * somebody who belongs to exactly one, the picker is then a screen with a
   * single button and no decision on it, shown on every single sign-in. So the
   * one case with no choice to make is made automatically; nought and several
   * both still go to the picker, where they belong.
   *
   * `attempted` is what stops a refused selection from looping: a membership
   * that has been revoked between the list read and the switch answers 403, and
   * without it the effect would re-run on every render forever.
   */
  const sole = memberships?.length === 1 ? (memberships[0] ?? null) : null
  const soleId = sole?.tenant.id ?? null
  const [selecting, setSelecting] = useState(false)
  const attempted = useRef<string | null>(null)

  useEffect(() => {
    if (!authenticated || activeId !== null || soleId === null) return
    if (attempted.current === soleId) return
    attempted.current = soleId
    setSelecting(true)
    void (async () => {
      await select(soleId)
      if (mounted.current) setSelecting(false)
    })()
  }, [authenticated, activeId, soleId, select])

  // Read during render, not after it. The effect above runs *after* the commit,
  // so without this there is one render reporting `ready` with no active
  // organisation — and `RequireTenant` redirects to the picker on it, which is
  // the exact frame the auto-selection exists to avoid. A refused selection
  // leaves `attempted` set and `selecting` false, so the redirect happens then
  // and the user chooses for themselves.
  const autoSelectPending =
    authenticated &&
    activeId === null &&
    soleId !== null &&
    (attempted.current !== soleId || selecting)

  const value = useMemo<TenantValue>(() => {
    const rows = memberships ?? []
    return {
      // `ready` for an anonymous visitor: there is nothing to wait for, and no
      // shell will ask — `RequireTenant` sits behind `RequireAuth`. A failure
      // is only `error` while there is no list to fall back on; a *reload* that
      // fails leaves the shell standing on what it already had.
      status: !authenticated
        ? 'ready'
        : autoSelectPending
          ? 'loading'
          : memberships !== null
            ? 'ready'
            : failure
              ? 'error'
              : 'loading',
      failure,
      memberships: rows,
      // Matched against the list rather than trusted from the session alone:
      // the session may point at an organisation this user has since been
      // removed from, and rendering a shell for one they cannot read would
      // fail on the first request instead of at the boundary.
      active: rows.find((row) => row.tenant.id === activeId) ?? null,
      select,
      adopt,
      reload,
    }
  }, [memberships, failure, autoSelectPending, authenticated, activeId, select, adopt, reload])

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}
